import type { ChatMessage } from "../hooks/useChat";
import type { ChatOrderPromotionAttachment } from "./chatPromotions";

export type { ChatMessage };

interface BuildOptimisticChatMessageInput {
  displayName: string;
  traderCode: string;
  text: string;
  now: number;
  clientMessageId: string;
  attachment?: ChatOrderPromotionAttachment | null;
}

interface ReplaceOptimisticChatMessageInput {
  messages: ChatMessage[];
  optimisticId: string;
  serverMessageId: string;
}

export function buildOptimisticChatMessage({
  displayName,
  traderCode,
  text,
  now,
  clientMessageId,
  attachment = null,
}: BuildOptimisticChatMessageInput): ChatMessage {
  const normalizedTraderCode = traderCode.trim().toUpperCase();
  return {
    id: `optimistic:${clientMessageId}`,
    user: displayName.trim() || normalizedTraderCode,
    traderCode: normalizedTraderCode,
    message: text,
    timestamp: now,
    attachment,
  };
}

export function replaceOptimisticChatMessage({
  messages,
  optimisticId,
  serverMessageId,
}: ReplaceOptimisticChatMessageInput): ChatMessage[] {
  const optimisticMessage = messages.find((message) => message.id === optimisticId);
  const withoutOptimistic = messages.filter((message) => message.id !== optimisticId);

  if (!optimisticMessage || withoutOptimistic.some((message) => message.id === serverMessageId)) {
    return sortChatMessages(withoutOptimistic);
  }

  return sortChatMessages([
    ...withoutOptimistic,
    {
      ...optimisticMessage,
      id: serverMessageId,
    },
  ]);
}

export function removeOptimisticChatMessage(messages: ChatMessage[], optimisticId: string): ChatMessage[] {
  return messages.filter((message) => message.id !== optimisticId);
}

export function dedupeChatMessages(messages: ChatMessage[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const message of messages) {
    if (!message.id) continue;
    byId.set(message.id, { ...byId.get(message.id), ...message });
  }
  return sortChatMessages(Array.from(byId.values()));
}

function sortChatMessages(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((a, b) => a.timestamp - b.timestamp);
}
